import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { AlertCircle } from 'react-feather';

import { Text, TEXT_VARIANTS } from 'ui/Typography';
import LessonBackToPath from './BackToPath';
import LessonTitle from './LessonTitle';
import SectionTitle from './SectionTitle';
import LessonHeaderAskTheExpert from './LessonHeaderAskTheExpert';
import EditLessonButton from './EditLessonButton';
import LessonHeaderSectionList from './LessonHeaderSectionList';

import LessonHeaderSectionButton from './LessonHeaderSectionButton';

const LessonHeader = ({
  title,
  section,
  sections,
  pathId,
  pathName,
  isPreviewing,
  currentSectionIndex,
  totalSectionCount,
  unansweredRequiredQuestionsCount = 0,
  isEmbed,
  lessonUrl,
  hasLessonExpert,
  hasEmail,
  hasCurrentUser,
  currentLearningPath,
  askTheExpertPostUrl,
  expertIconName,
  expertName,
  metadata,
}) => {
  const isPathLesson = !!currentLearningPath;
  const [isHovered, setIsHovered] = useState(false);
  const [isSectionListOpen, setIsSectionListOpen] = useState(false);
  const [sectionListOpenedByKeyboard, setSectionListOpenedByKeyboard] =
    useState(false);

  const onOpenSectionlist = () => {
    setIsSectionListOpen(true);
    setSectionListOpenedByKeyboard(false);
  };

  const onCloseSectionList = () => {
    setIsSectionListOpen(false);
    setSectionListOpenedByKeyboard(false);
  };

  return (
    <header
      className="LessonHeader"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="LessonHeader-layout">
        {isPathLesson && (
          <div className="d-f jc-sb">
            <LessonBackToPath
              pathId={pathId}
              pathName={pathName}
              isPreviewing={isPreviewing}
            />
            <LessonHeaderAskTheExpert
              hasLessonExpert={hasLessonExpert}
              hasEmail={hasEmail}
              isPreviewing={isPreviewing}
              askTheExpertPostUrl={askTheExpertPostUrl}
              sectionId={section?.id}
              expertIconName={expertIconName}
              expertName={expertIconName}
              lessonTitle={title}
            />
          </div>
        )}
        <div className="d-f jc-sb">
          <div className="wb-bw ml-8">
            <LessonTitle
              title={title}
              isEmbed={isEmbed}
              externalLink={lessonUrl}
            />
            <SectionTitle title={section?.title} />
          </div>
          <div className="d-f">
            {!isPathLesson && (
              <LessonHeaderAskTheExpert
                hasLessonExpert={hasLessonExpert}
                hasEmail={hasEmail}
                isPreviewing={isPreviewing}
                askTheExpertPostUrl={askTheExpertPostUrl}
                sectionId={section?.id}
                expertIconName={expertIconName}
                expertName={expertIconName}
                lessonTitle={title}
              />
            )}
            {metadata?.permissions?.canEditLesson && (
              <EditLessonButton editLessonSectionUrl={section?.editUrl} />
            )}
          </div>
        </div>
        <div className="d-f">
          <LessonHeaderSectionButton
            className="mr-8"
            currentSectionIndex={section?.index}
            totalSectionCount={totalSectionCount}
            hovered={isHovered}
            onMouseDown={e => {
              // Prevents mouse click focus ring
              e.preventDefault();
            }}
            onClick={() => !isSectionListOpen && onOpenSectionlist()}
            onKeyUp={e => {
              if (e.keyCode === 13) {
                setSectionListOpenedByKeyboard(true);
              }
            }}
          />
          {unansweredRequiredQuestionsCount > 0 && (
            <Text className="d-f ai-c" variant={TEXT_VARIANTS.INSTRUX_ERROR}>
              <AlertCircle size="1.25rem" className="mr-8" />{' '}
              {t('learning.lessons.required_questions', {
                count: unansweredRequiredQuestionsCount,
              })}
            </Text>
          )}
        </div>
      </div>
      <LessonHeaderSectionList
        isOpen={isSectionListOpen}
        focusOnOpen={sectionListOpenedByKeyboard}
        onClose={() => isSectionListOpen && onCloseSectionList()}
        section={section}
        sections={sections}
        isPreviewing={isPreviewing}
        hasCurrentUser={hasCurrentUser}
      />
    </header>
  );
};

LessonHeader.propTypes = {
  title: PropTypes.string,
  pathId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  pathName: PropTypes.string,
  totalSectionCount: PropTypes.number,
  unansweredRequiredQuestionsCount: PropTypes.number,
  isEmbed: PropTypes.bool,
  hasLessonExpert: PropTypes.bool,
  hasEmail: PropTypes.bool,
  hasCurrentUser: PropTypes.bool,
  isPreviewing: PropTypes.bool,
  lessonUrl: PropTypes.string,
  askTheExpertPostUrl: PropTypes.string,
  section: PropTypes.shape({
    name: PropTypes.string,
    index: PropTypes.number,
    editUrl: PropTypes.string,
    id: PropTypes.number,
  }),
  expertIconName: PropTypes.string,
  expertName: PropTypes.string,
  metadata: PropTypes.shape({
    permissions: PropTypes.shape({
      canEditLesson: PropTypes.bool,
    }),
  }),
};

export default props => <LessonHeader {...props} />;
